import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { IValuationScrape, IValuationScrapeListResponse, IValuationScrapeSingleResponse } from "../models/ValuationScrape";
import apiConfig from "../utils/apiConfig";

const PENDING_STATUSES = ["Queued", "Running"];

export default function useValuationScrape(vehicleId: string | undefined, componentName = "useValuationScrape") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const scrapes = useQuery({
    queryKey: ["valuation-scrapes", vehicleId],
    queryFn: () => get<IValuationScrapeListResponse>(`/valuation-scrape/vehicle/${vehicleId}`),
    enabled: !!vehicleId,
    refetchInterval: (data) => {
      const list = data?.data ?? [];
      const hasPending = list.some((s: IValuationScrape) => PENDING_STATUSES.includes(s.status));
      return hasPending ? 4000 : false;
    },
  });

  const latestScrape: IValuationScrape | undefined = scrapes.data?.data?.[0];
  const isPending = !!latestScrape && PENDING_STATUSES.includes(latestScrape.status);

  const initiate = useMutation(
    () => {
      if (!vehicleId) throw new Error("Missing vehicleId");
      return post<object, IValuationScrapeSingleResponse>(`/valuation-scrape/vehicle/${vehicleId}/initiate`, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["valuation-scrapes", vehicleId]);
      },
    }
  );

  return {
    scrapes,
    latestScrape,
    isPending,
    initiate,
  };
}
